// ═══════════════════════════════════════════════════════════════
// CATÁLOGO DE TIPOS DE TAREA + Resolución de SLA
// ═══════════════════════════════════════════════════════════════
//
// Colección: tiposTarea (top-level)
//   { codigo, nombre, descripcion, moduloOrigen, dashboardOrigen,
//     slaDefaultMinutos, ventanaAlertaMinutos,
//     prioridadDefault, permitirAgrupacion, activo, orden,
//     esfuerzo (1-4), etiqueta ('no_perder'|'ganar'|'habilitadora'),
//     emiteEventoBitacora }
//
//   permitirAgrupacion (bool, default true):
//     true  → el modal permite crear 1 tarea agrupada o N individuales (toggle visible)
//     false → siempre se crean N tareas individuales (toggle oculto)
//
// Overrides: empresas/{empId}/slaOverrides
//   { tipoTareaId, userId (null=empresa), slaMinutos,
//     ventanaAlertaMinutos?, definidoPor }
//
// Cascada de resolución:
//   1. Usuario (override con userId + tipoTareaId)
//   2. Empresa (override con userId=null + tipoTareaId)
//   3. Plataforma (slaDefaultMinutos del catálogo)
//   4. Sin SLA (null → no se trackea)
// ═══════════════════════════════════════════════════════════════

import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc, query, where,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { forPlatformCreate, forUpdate } from './dbHelpers'

// ──────── ABM del catálogo ────────

/** Listar todos los tipos de tarea (opcionalmente solo activos). */
export async function listarTiposTarea({ soloActivos = true } = {}) {
  const snap = await getDocs(collection(db, COLLECTIONS.TIPOS_TAREA))
  let list = snap.docs.map(docConId)
  if (soloActivos) list = list.filter(t => t.activo !== false)
  list.sort((a, b) => (a.orden || 0) - (b.orden || 0))
  return list
}

/** Obtener un tipo de tarea por ID. */
export async function getTipoTarea(tipoTareaId) {
  const snap = await getDoc(doc(db, COLLECTIONS.TIPOS_TAREA, tipoTareaId))
  return snap.exists() ? docConId(snap) : null
}

/** Buscar tipo de tarea por código (ej: 'pausar_publicidad'). */
export async function getTipoTareaPorCodigo(codigo) {
  const q = query(collection(db, COLLECTIONS.TIPOS_TAREA), where('codigo', '==', codigo))
  const snap = await getDocs(q)
  if (snap.empty) return null
  const d = snap.docs[0]
  return docConId(d)
}

/** Crear o actualizar un tipo de tarea. */
export async function upsertTipoTarea(tipoTareaId, datos) {
  const data = {
    codigo: datos.codigo || null,
    nombre: datos.nombre || 'Tipo de tarea',
    descripcion: datos.descripcion || '',
    moduloOrigen: datos.moduloOrigen || null,
    dashboardOrigen: datos.dashboardOrigen || null,
    slaDefaultMinutos: datos.slaDefaultMinutos != null ? Number(datos.slaDefaultMinutos) : null,
    ventanaAlertaMinutos: Number(datos.ventanaAlertaMinutos) || 15,
    prioridadDefault: datos.prioridadDefault || 'normal',
    permitirAgrupacion: datos.permitirAgrupacion !== false, // default true
    activo: datos.activo !== false,
    orden: Number(datos.orden) || 100,
  }
  // Campos del modelo canónico Postventa MVP (docs/modelo-tareas.md) + agente
  // global. Solo se escriben si vienen DEFINIDOS: el modal de SLAsPage no
  // edita cierre/categoria/legacy/redirigeA/emiteEventoBitacora, y un save
  // desde ahí no debe pisarlos (antes se limpiaban a null — bug jul-2026).
  if (datos.cierre !== undefined) data.cierre = datos.cierre || null             // 'atomico' | 'granular' | null
  if (datos.categoria !== undefined) data.categoria = datos.categoria || null   // 'estructural' | 'operacional' | 'profundidad' | null
  if (datos.legacy !== undefined) data.legacy = !!datos.legacy                   // tipos legacy (ocultos en UI por default)
  if (datos.redirigeA !== undefined) data.redirigeA = datos.redirigeA || null   // código canónico al que el legacy redirige
  if (datos.emiteEventoBitacora !== undefined) data.emiteEventoBitacora = !!datos.emiteEventoBitacora
  // Jul 2026 — Agente global (impacto × esfuerzo):
  //   esfuerzo: 1 prender/apagar · 2 comparar · 3 modificar · 4 gestionar/profundizar
  //   etiqueta: 'no_perder' | 'ganar' | 'habilitadora' (1º no perder plata, 2º ganar)
  if (datos.esfuerzo !== undefined) data.esfuerzo = (datos.esfuerzo != null && datos.esfuerzo !== '') ? Number(datos.esfuerzo) : null
  if (datos.etiqueta !== undefined) data.etiqueta = datos.etiqueta || null
  // cuotaProduccion: máx ítems por tarea generada por el agente (lotes).
  // Vacío = sin límite. Mientras haya tarea abierta del tipo, no genera otro lote.
  if (datos.cuotaProduccion !== undefined) data.cuotaProduccion = (datos.cuotaProduccion != null && datos.cuotaProduccion !== '') ? Number(datos.cuotaProduccion) : null
  // Tipo que acumula hallazgos de varias fuentes en la misma publicación (merge).
  if (datos.fusionaPorPublicacion !== undefined) data.fusionaPorPublicacion = !!datos.fusionaPorPublicacion
  // Sección que el agente rutea a CASO (no crea tarea estructural; nuclea en una tarea-resumen).
  if (datos.sugiereCaso !== undefined) data.sugiereCaso = !!datos.sugiereCaso
  if (datos.tipoCasoSugerido !== undefined) data.tipoCasoSugerido = datos.tipoCasoSugerido || null
  if (!tipoTareaId) {
    const payload = forPlatformCreate(data)
    const ref = await addDoc(collection(db, COLLECTIONS.TIPOS_TAREA), payload)
    return { id: ref.id, ...payload }
  }
  const payload = forUpdate(data)
  await updateDoc(doc(db, COLLECTIONS.TIPOS_TAREA, tipoTareaId), payload)
  return { id: tipoTareaId, ...payload }
}

/** Desactivar (soft delete) un tipo de tarea. */
export async function desactivarTipoTarea(tipoTareaId) {
  await updateDoc(doc(db, COLLECTIONS.TIPOS_TAREA, tipoTareaId), forUpdate({ activo: false }))
}

// ──────── Overrides por empresa/usuario ────────

/** Listar overrides de SLA de una empresa. */
export async function listarSlaOverrides(empresaId) {
  const snap = await getDocs(collection(db, COLLECTIONS.EMPRESAS, empresaId, 'slaOverrides'))
  return snap.docs.map(docConId)
}

/** Crear o actualizar un override de SLA. */
export async function upsertSlaOverride(empresaId, overrideId, datos) {
  const data = {
    tipoTareaId: datos.tipoTareaId,
    userId: datos.userId || null, // null = override de empresa
    slaMinutos: Number(datos.slaMinutos),
    ventanaAlertaMinutos: datos.ventanaAlertaMinutos != null ? Number(datos.ventanaAlertaMinutos) : null,
    definidoPor: datos.definidoPor || null,
  }
  const colRef = collection(db, COLLECTIONS.EMPRESAS, empresaId, 'slaOverrides')
  if (!overrideId) {
    const payload = { ...data, creadoEn: serverTimestamp(), actualizadoEn: serverTimestamp() }
    const ref = await addDoc(colRef, payload)
    return { id: ref.id, ...payload }
  }
  const payload = { ...data, actualizadoEn: serverTimestamp() }
  await updateDoc(doc(db, COLLECTIONS.EMPRESAS, empresaId, 'slaOverrides', overrideId), payload)
  return { id: overrideId, ...payload }
}

/** Eliminar un override de SLA. */
export async function eliminarSlaOverride(empresaId, overrideId) {
  const { deleteDoc: del } = await import('firebase/firestore')
  await del(doc(db, COLLECTIONS.EMPRESAS, empresaId, 'slaOverrides', overrideId))
}

// ──────── Resolución de SLA (cascada) ────────

/**
 * Resuelve el SLA que aplica para una tarea dada.
 * Cascada: usuario → empresa → plataforma → null.
 *
 * @param {string} empresaId
 * @param {string} tipoTareaId  — ID del tipo de tarea (o código, resuelto antes)
 * @param {string|null} userId  — responsable (para buscar override de usuario)
 * @returns {{ slaMinutos, ventanaAlertaMinutos } | null}
 */
export async function resolverSLA(empresaId, tipoTareaId, userId = null) {
  if (!tipoTareaId) return null

  // Cargar tipo de tarea (defaults de plataforma)
  const tipo = await getTipoTarea(tipoTareaId)
  if (!tipo) return null

  const defaults = {
    slaMinutos: tipo.slaDefaultMinutos,
    ventanaAlertaMinutos: tipo.ventanaAlertaMinutos || 15,
  }

  // Si no hay empresa, devolver defaults de plataforma
  if (!empresaId) return defaults.slaMinutos != null ? defaults : null

  // Cargar overrides de la empresa
  const overrides = await listarSlaOverrides(empresaId)
  const relevantes = overrides.filter(o => o.tipoTareaId === tipoTareaId)

  // Buscar override de usuario
  if (userId) {
    const userOverride = relevantes.find(o => o.userId === userId)
    if (userOverride) {
      return {
        slaMinutos: userOverride.slaMinutos,
        ventanaAlertaMinutos: userOverride.ventanaAlertaMinutos ?? defaults.ventanaAlertaMinutos,
      }
    }
  }

  // Buscar override de empresa
  const empOverride = relevantes.find(o => !o.userId)
  if (empOverride) {
    return {
      slaMinutos: empOverride.slaMinutos,
      ventanaAlertaMinutos: empOverride.ventanaAlertaMinutos ?? defaults.ventanaAlertaMinutos,
    }
  }

  // Plataforma defaults
  return defaults.slaMinutos != null ? defaults : null
}

// ──────── Tracking automático de tiempos ────────

/**
 * Calcula el tracking de tiempos a aplicar cuando cambia el estado de una tarea.
 * No escribe a Firestore; devuelve un objeto de updates para mergear.
 *
 * @param {Object} tareaActual — datos actuales de la tarea
 * @param {string} nuevoEstado
 * @returns {Object} campos a mergear en el update de Firestore
 */
export function calcularTrackingUpdates(tareaActual, nuevoEstado) {
  const updates = {}
  const ahora = new Date()
  const estadoActual = tareaActual.estado
  const intervalos = [...(tareaActual.intervalos || [])]

  // ── Iniciar tracking: pasa a en_curso ──
  if (nuevoEstado === 'en_curso' && estadoActual !== 'en_curso') {
    // Agregar nuevo intervalo abierto
    intervalos.push({ inicio: ahora.toISOString(), fin: null })
    updates.intervalos = intervalos
    updates.trackingActivo = true
    // Primer inicio absoluto
    if (!tareaActual.iniciadoEn) {
      updates.iniciadoEn = serverTimestamp()
    }
  }

  // ── Pausar tracking: sale de en_curso (pausada, en_revision, etc.) ──
  if (estadoActual === 'en_curso' && nuevoEstado !== 'en_curso') {
    // Cerrar último intervalo abierto
    const ultimoIdx = intervalos.length - 1
    if (ultimoIdx >= 0 && !intervalos[ultimoIdx].fin) {
      intervalos[ultimoIdx] = { ...intervalos[ultimoIdx], fin: ahora.toISOString() }
      updates.intervalos = intervalos
    }
    updates.trackingActivo = false
  }

  // ── Completar: calcular tiempo real ──
  if (nuevoEstado === 'cerrado' || nuevoEstado === 'cancelada') {
    // Cerrar intervalo abierto si quedó
    const ultimoIdx = intervalos.length - 1
    if (ultimoIdx >= 0 && !intervalos[ultimoIdx].fin) {
      intervalos[ultimoIdx] = { ...intervalos[ultimoIdx], fin: ahora.toISOString() }
      updates.intervalos = intervalos
    }
    updates.trackingActivo = false

    // Calcular tiempo real (suma de intervalos en minutos)
    if (intervalos.length > 0) {
      let totalMs = 0
      for (const iv of intervalos) {
        if (iv.inicio && iv.fin) {
          totalMs += new Date(iv.fin).getTime() - new Date(iv.inicio).getTime()
        }
      }
      updates.tiempoRealMinutos = Math.round(totalMs / 60000)
    }

    if (nuevoEstado === 'cerrado') {
      updates.completadoEn = serverTimestamp()
    }
  }

  // ── Reabrir: limpiar completado pero mantener intervalos ──
  if ((estadoActual === 'cerrado' || estadoActual === 'cancelada') &&
      (nuevoEstado === 'pendiente' || nuevoEstado === 'en_curso')) {
    updates.completadoEn = null
    updates.tiempoRealMinutos = null
  }

  return updates
}

// ──────── Exponer en window ────────

if (typeof window !== 'undefined') {
  window.__tiposTarea = {
    listarTiposTarea,
    getTipoTarea,
    getTipoTareaPorCodigo,
    upsertTipoTarea,
    desactivarTipoTarea,
    listarSlaOverrides,
    upsertSlaOverride,
    eliminarSlaOverride,
    resolverSLA,
  }
}
